/**
 * The roster: which canvas agent owns each `agent_id` the code writes (Agent
 * Brains section 10, the "role charter" layer L0). T3: a human-edited
 * constant. A model never changes who owns an id.
 *
 * Three things are kept apart on purpose:
 *  - `CANVAS_AGENTS`: the seven agents of the canvas, each with a role charter
 *    (roleCharters.ts).
 *  - `DUTIES`: the named duties a canvas agent splits into where one agent
 *    holds more than one job (nurture holds the conversation, qualification
 *    and the calendar; autolearn holds learning and onboarding).
 *  - `AGENT_ID_OWNER`: every `agent_id` an agent or handler writes today,
 *    mapped to an agent and, where it has one, a duty.
 *  - `SYSTEM_ACTORS`: the ids that are not agents at all (ingest, worker,
 *    owner, gateway, CLIs). They own no role charter.
 *
 * `agentRoster.test.ts` scans the repository for every agent id literal and
 * fails on one that is in neither map, so a new agent id cannot ship without
 * being placed here.
 */
import { deepFreeze } from "./deepFreeze";

export const CANVAS_AGENTS = [
  "research",
  "reputation",
  "shepherd",
  "analyst",
  "nurture",
  "autolearn",
  "campaign",
] as const;
export type CanvasAgentId = (typeof CANVAS_AGENTS)[number];

export const DUTIES = ["nurture.reply", "nurture.qualify", "nurture.schedule", "autolearn.onboard"] as const;
export type DutyId = (typeof DUTIES)[number];

export interface AgentIdOwner {
  readonly agent: CanvasAgentId;
  readonly duty?: DutyId;
}

const reply: AgentIdOwner = { agent: "nurture", duty: "nurture.reply" };
const schedule: AgentIdOwner = { agent: "nurture", duty: "nurture.schedule" };

/**
 * Every agent id the code writes, per owner. Where the design lists an id
 * (reply, reply.transmit, routing, consult-intent, escalation_sla,
 * missed_call_text_back, booking, appointment_reminder_sweep, follow_up_sweep,
 * followups, shepherd, campaign_sweep, campaign, review_ask_sweep,
 * research.enrichment, ghost_labeler, outcome_digest, amber_digest) it is
 * placed as the design says. memory-save-gate and vip_alert are placed by
 * what they do (nurture.reply); the T3 report records that as an assumption.
 */
export const AGENT_ID_OWNER: Readonly<Record<string, AgentIdOwner>> = deepFreeze<Record<string, AgentIdOwner>>({
  // nurture.reply: the live conversation, its routing, holding and escalation.
  reply,
  "reply.transmit": reply,
  routing: reply,
  "consult-intent": reply,
  escalation_sla: reply,
  missed_call_text_back: reply,
  // V6: an opt-out is a conversation-level decision (TA line), so the suppression detector is owned by nurture.reply.
  suppress: reply,
  // Memory writes happen inside the conversation (section 6 save-gate).
  "memory-save-gate": reply,
  // The owner is told a VIP wrote in: a conversation event, owned by the reply duty.
  vip_alert: reply,
  // X2 CATALOG: the product and media sync tasks serve the live conversation's composer rail (what the shop
  // can show a customer), so the catalog id sits under nurture.reply. A T3 assumption (brief, X2 placement).
  catalog: reply,
  // X3 RAIL: the rail.prefill task suggests products and media for the draft set of a live conversation; it
  // writes rail_suggestions and rail.prefilled under this id and sends nothing. Placed by what it serves.
  rail: reply,

  // nurture.qualify: score and route the lead, nobody rejected (U3, Agent Brains 5.3).
  qualify: { agent: "nurture", duty: "nurture.qualify" },

  // nurture.schedule: the calendar.
  booking: schedule,
  appointment_reminder_sweep: schedule,
  // V3 SCHEDULER: consult.book / consult.brief, their proposals and briefs, and the booking confirmation.
  scheduler: schedule,

  // shepherd: nothing rots.
  follow_up_sweep: { agent: "shepherd" },
  followups: { agent: "shepherd" },
  shepherd: { agent: "shepherd" },
  // V1 STAGES: the pipeline.stage_watch handler (stage.changed, stage.sla_breached, hygiene report).
  "shepherd-stages": { agent: "shepherd" },

  // campaign: one-to-many waves.
  campaign_sweep: { agent: "campaign" },
  campaign: { agent: "campaign" },
  // V5: the weekly campaign.plan handler, the comment-to-DM trigger and the marketing consent store.
  "campaign-operator": { agent: "campaign" },

  // reputation: review asks, recovery, responses.
  review_ask_sweep: { agent: "reputation" },
  // V4: the review.cycle handler (reviews in, response drafts, HEARD requests, synthesis).
  reputation: { agent: "reputation" },

  // research: the lead card.
  "research.enrichment": { agent: "research" },
  // U1: the lead.research handler writes lead.researched and the lead_card handoff under this id;
  // U2's VIP detection and flag writer write under it too.
  research: { agent: "research" },

  // autolearn: labelled outcomes in, proposals out.
  ghost_labeler: { agent: "autolearn" },

  // autolearn also writes under its own id: the learner batch (learn.* events).
  autolearn: { agent: "autolearn" },
  // W3 LESSONS: the lessons.write handler and the weekly consolidation (lesson.* events).
  lessons: { agent: "autolearn" },

  // W4 SIGNALS: the signal.attribute handler (correction attribution, invisible failures,
  // the flag-off attribution stamp) writes under the plain id signals, owned by autolearn.
  signals: { agent: "autolearn" },

  // W6 GRADUATE: the weekly graduation.review handler writes autonomy.changed and its ledger row
  // under this id. It reports eligibility and demotes automatically; it never promotes (B6).
  graduation: { agent: "autolearn" },

  // analyst: measure and explain.
  outcome_digest: { agent: "analyst" },
  amber_digest: { agent: "analyst" },
  // W1 ANALYST: analyst.weekly, analyst.ask and the explain replay write under this id.
  analyst: { agent: "analyst" },
  // W7 SCOREBOARD: the weekly precision scoreboard (deterministic aggregates, no model).
  scoreboard: { agent: "analyst" },

  // X4 TEAM: the team.route handler hands a new conversation to a person (thread.assigned). It serves the
  // live conversation but has no reply duty of its own, so it is owned by nurture with no duty (the X brief;
  // the T3 report records the placement as an assumption).
  team_route: { agent: "nurture" },
});

/**
 * Ids that are not agents. Exact ids here; `SYSTEM_ACTOR_PREFIXES` covers the
 * families (`ingest*`, `deletion-*`, `evals.*`, `pipeline.*`).
 */
export const SYSTEM_ACTORS: readonly string[] = Object.freeze([
  "ingest",
  "ingest.reconcile",
  "worker",
  "gateway",
  "owner",
  "publish-artifacts",
  "artifacts-cli",
  "heartbeat",
  "handoff_sweep",
  "situation",
  "draft_notify",
  "draft_latency",
  "compliance",
  "api-http",
  "webhook-route",
  "deletion-route",
  "deletion-ledger",
  "evals.canary",
  // R8.1: the real `pnpm eval` run writes graduation evidence (eval.scenario_measured, redteam.run_recorded)
  // under this id. Not evals.canary, which raises drift alerts and measures nothing a graduation reads.
  "evals.suite",
  "pipeline.labeler",
  "pipeline.label-drift",
  "verification_live_reply",
]);

export const SYSTEM_ACTOR_PREFIXES: readonly string[] = Object.freeze(["ingest", "deletion-", "evals.", "pipeline."]);

export function isSystemActor(agentId: string): boolean {
  return SYSTEM_ACTORS.includes(agentId) || SYSTEM_ACTOR_PREFIXES.some((p) => agentId.startsWith(p));
}

/** The owner of an agent id, or null for a system actor or an id nobody placed. */
export function ownerOfAgentId(agentId: string): AgentIdOwner | null {
  return Object.prototype.hasOwnProperty.call(AGENT_ID_OWNER, agentId) ? (AGENT_ID_OWNER[agentId] ?? null) : null;
}

export function isCanvasAgent(value: string): value is CanvasAgentId {
  return (CANVAS_AGENTS as readonly string[]).includes(value);
}

export function isDuty(value: string): value is DutyId {
  return (DUTIES as readonly string[]).includes(value);
}

/** The canvas agent a duty belongs to: the part before the dot. */
export function agentOfDuty(duty: DutyId): CanvasAgentId {
  return duty.split(".")[0] as CanvasAgentId;
}
